import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { API } from "../utils/api";
import {
  Menu,
  X,
  MapPin,
  Globe,
  MessageSquare,
  Star,
  LogOut,
  Shield,
  User,
  Heart,
  PlusCircle,
  LayoutDashboard,
} from "lucide-react";

const Header = ({ scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingRatings, setPendingRatings] = useState([]);

  const login = () => navigate("/auth");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch notification count
  useEffect(() => {
    if (user) {
      const fetchNotifications = async () => {
        try {
          const response = await axios.get(`${API}/notifications/unread-count`);
          setUnreadCount(response.data.unread_messages);
          // Fetch pending ratings for modal
          const ratingsResponse = await axios.get(`${API}/pending-ratings`);
          setPendingRatings(ratingsResponse.data);
        } catch (error) {
          console.error("Error fetching notifications:", error);
        }
      };
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const navItems = isLandingPage
    ? [
        { label: t("nav_announcements"), id: "posts", action: () => navigate("/annonces") },
        { label: t("nav_marketplace"), id: "marketplace", action: () => navigate("/marketplace") },
        { label: t("nav_how_it_works"), id: "how-it-works", action: () => scrollToSection?.("how-it-works") },
        { label: t("nav_services"), id: "services", action: () => scrollToSection?.("services") },
      ]
    : [
        { label: t("nav_announcements"), action: () => navigate("/annonces") },
        { label: t("nav_marketplace"), action: () => navigate("/marketplace") },
        { label: t("nav_dashboard"), action: () => navigate("/dashboard") },
        ...(user?.is_admin ? [{ label: "Admin", action: () => navigate("/admin"), isAdmin: true }] : []),
      ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "header-glass bg-white/80 border-b border-slate-200/50 shadow-sm" : "bg-white/90"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a
            href="/"
            data-testid="logo"
            className="flex items-center space-x-2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Outfit']">LëtzServices</span>
          </a>

          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id || item.label}
                data-testid={`nav-${item.id || item.label.toLowerCase()}`}
                onClick={item.action}
                className={`transition-colors font-medium ${
                  item.isAdmin 
                    ? "text-sky-600 hover:text-sky-700 flex items-center gap-1" 
                    : "text-slate-600 hover:text-sky-600"
                }`}
              >
                {item.isAdmin && <Shield className="w-4 h-4" />}
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-sky-600 border border-slate-200 rounded-lg hover:border-sky-300 transition-all"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
            
            {user ? (
              <>
                <button
                  onClick={() => navigate("/messages")}
                  className="relative p-2 text-slate-600 hover:text-sky-600"
                  data-testid="messages-btn"
                >
                  <MessageSquare className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                {pendingRatings.length > 0 && (
                  <button
                    onClick={() => navigate("/pending-ratings")}
                    className="relative p-2 text-slate-600 hover:text-yellow-600"
                    data-testid="pending-ratings-btn"
                    title={lang === "fr" ? "Notations en attente" : "Pending ratings"}
                  >
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingRatings.length}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center space-x-2"
                  data-testid="profile-btn"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.picture?.startsWith("letzservices/") ? `${API}/files/${user.picture}` : user.picture} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                </button>
                <Button
                  variant="outline"
                  onClick={logout}
                  className="border-slate-200"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("nav_logout")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="header-login"
                  variant="outline"
                  onClick={login}
                  className="border-sky-600 text-sky-600 hover:bg-sky-50 rounded-xl px-5 py-2 font-medium"
                >
                  {t("nav_login")}
                </Button>
                <Button
                  data-testid="header-cta"
                  onClick={login}
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-6 py-2 font-bold shadow-lg shadow-sky-600/20"
                >
                  {t("nav_create_account")}
                </Button>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-button"
            aria-label={t("nav_menu") || "Menu"}
            className="md:hidden relative p-2 -mr-2 touch-manipulation"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            {!isMobileMenuOpen && user && (unreadCount > 0 || pendingRatings.length > 0) && (
              <span
                data-testid="mobile-menu-badge"
                className="absolute top-1 right-1 bg-red-500 rounded-full w-2.5 h-2.5 border-2 border-white"
              />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-4 py-4 space-y-1">
              {/* User profile summary (connected) */}
              {user && (
                <button
                  data-testid="mobile-profile-summary"
                  onClick={() => {
                    navigate("/profile");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 bg-sky-50 rounded-xl mb-2 hover:bg-sky-100 transition-colors"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user.picture?.startsWith("letzservices/") ? `${API}/files/${user.picture}` : user.picture} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{t("nav_view_profile") || "Voir mon profil"}</div>
                  </div>
                </button>
              )}

              {/* Main navigation */}
              {navItems.map((item) => (
                <button
                  key={item.id || item.label}
                  data-testid={`mobile-nav-${item.id || item.label.toLowerCase()}`}
                  onClick={() => {
                    item.action();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-2 transition-colors ${
                    item.isAdmin
                      ? "text-sky-600 bg-sky-50 hover:bg-sky-100"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {item.isAdmin && <Shield className="w-4 h-4" />}
                  {item.label}
                </button>
              ))}

              {/* Connected-user shortcuts */}
              {user && (
                <>
                  <div className="border-t border-slate-100 my-2"></div>

                  <button
                    data-testid="mobile-create-post"
                    onClick={() => {
                      navigate("/posts/new");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <PlusCircle className="w-5 h-5 text-sky-600" />
                    {t("nav_create_post") || "Créer une annonce"}
                  </button>

                  <button
                    data-testid="mobile-dashboard"
                    onClick={() => {
                      navigate("/dashboard");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <LayoutDashboard className="w-5 h-5 text-slate-500" />
                    {t("nav_dashboard") || "Tableau de bord"}
                  </button>

                  <button
                    data-testid="mobile-messages"
                    onClick={() => {
                      navigate("/messages");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <MessageSquare className="w-5 h-5 text-slate-500" />
                    <span className="flex-1">{t("nav_messages") || "Messages"}</span>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>

                  <button
                    data-testid="mobile-favorites"
                    onClick={() => {
                      navigate("/favorites");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <Heart className="w-5 h-5 text-slate-500" />
                    {t("nav_favorites") || "Favoris"}
                  </button>

                  {pendingRatings.length > 0 && (
                    <button
                      data-testid="mobile-pending-ratings"
                      onClick={() => {
                        navigate("/pending-ratings");
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                    >
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="flex-1">{t("nav_pending_ratings") || "Notations en attente"}</span>
                      <span className="bg-yellow-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                        {pendingRatings.length}
                      </span>
                    </button>
                  )}

                  <button
                    data-testid="mobile-profile"
                    onClick={() => {
                      navigate("/profile");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <User className="w-5 h-5 text-slate-500" />
                    {t("nav_profile") || "Mon profil"}
                  </button>
                </>
              )}

              <div className="border-t border-slate-100 my-2"></div>

              {/* Language toggle - always visible */}
              <button
                data-testid="mobile-lang-toggle"
                onClick={() => {
                  toggleLang();
                }}
                className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
              >
                <Globe className="w-5 h-5 text-slate-500" />
                <span className="flex-1">
                  {lang === "fr" ? "Langue : Français" : "Language: English"}
                </span>
                <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2 py-1 rounded">
                  {lang === "fr" ? "→ EN" : "→ FR"}
                </span>
              </button>

              {/* Auth buttons */}
              {user ? (
                <Button
                  data-testid="mobile-logout-btn"
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full mt-2"
                  variant="outline"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("nav_logout")}
                </Button>
              ) : (
                <div className="space-y-2 pt-2">
                  <Button
                    data-testid="mobile-login-btn"
                    onClick={() => {
                      login();
                      setIsMobileMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-sky-600 text-sky-600"
                  >
                    {t("nav_login")}
                  </Button>
                  <Button
                    data-testid="mobile-signup-btn"
                    onClick={() => {
                      login();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {t("nav_create_account")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
